import { Link } from 'react-router-dom'
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Download,
  Heart,
  Library,
  Search,
  Sparkles,
  Star,
  Target
} from 'lucide-react'
import SiteHeader from './SiteHeader'
import NewReleasesFeed from './home/NewReleasesFeed'

const APP_STORE_URL = 'https://apps.apple.com/gb/app/bukito/id6756301072'

const featureCards = [
  {
    icon: Target,
    title: 'Track reading without pressure',
    copy: 'Log shared reading, favourite stories and small wins from baby books through early chapter books.',
    color: 'bg-primary-500'
  },
  {
    icon: Library,
    title: 'Use ready-made book lists',
    copy: 'Browse curated lists for age, mood and reading stage when you need a better answer than another search tab.',
    color: 'bg-pink-500'
  },
  {
    icon: Heart,
    title: 'Keep motivation gentle',
    copy: 'Help reading feel warm and repeatable with family-friendly progress moments children can understand.',
    color: 'bg-yellow-500 text-text-primary'
  }
]

const Homepage = () => (
  <div className="min-h-screen bg-bg-main">
    <SiteHeader />

    <section className="overflow-hidden border-b border-primary-100 bg-bg-surface">
      <div className="container-custom py-12 sm:py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_460px] lg:items-center">
          <div className="max-w-3xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-lg bg-primary-50 px-3 py-2 text-sm font-bold text-primary-700">
              <BookOpen size={17} />
              Reading companion for ages 0-8
            </div>
            <h1 className="text-5xl font-extrabold leading-[1.04] text-text-primary sm:text-6xl lg:text-7xl">
              Make family reading easier to choose, track and keep going.
            </h1>
            <p className="mt-6 max-w-2xl text-xl leading-relaxed text-text-secondary">
              Bukito helps parents discover good books, build reading routines and keep progress visible without turning story time into homework.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-lg bg-primary-500 px-7 py-4 text-base font-bold text-white shadow-button transition-colors hover:bg-primary-600"
              >
                <Download size={20} />
                Download free app
              </a>
              <Link
                to="/lists"
                className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-lg border-2 border-primary-500 px-7 py-4 text-base font-bold text-primary-700 transition-colors hover:bg-primary-50"
              >
                Browse book lists
                <ArrowRight size={20} />
              </Link>
            </div>

            <div className="mt-8 grid max-w-xl grid-cols-1 gap-3 text-sm font-bold text-text-secondary sm:grid-cols-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={17} className="text-primary-500" />
                Built for parents
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={17} className="text-pink-500" />
                Kid-safe browsing
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={17} className="text-yellow-700" />
                Free to start
              </div>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-[430px] lg:mx-0">
            <div className="rounded-lg border border-gray-100 bg-bg-soft p-4 shadow-large">
              <div className="grid grid-cols-[0.82fr_1fr] gap-3">
                <div className="self-end rounded-[1.8rem] bg-gray-900 p-2 shadow-large">
                  <div className="relative aspect-[9/19.5] overflow-hidden rounded-[1.35rem] bg-bg-surface">
                    <img
                      src="/screenshot.jpg"
                      alt="Bukito app reading tracker screen"
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute left-1/2 top-0 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-gray-900" />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-[1.8rem] bg-gray-900 p-2 shadow-large">
                    <div className="relative aspect-[9/16] overflow-hidden rounded-[1.35rem] bg-bg-surface">
                      <img
                        src="/screenshot2.jpg"
                        alt="Bukito app book discovery screen"
                        className="h-full w-full object-cover object-top"
                      />
                      <div className="absolute left-1/2 top-0 h-4 w-20 -translate-x-1/2 rounded-b-2xl bg-gray-900" />
                    </div>
                  </div>

                  <div className="rounded-lg bg-bg-surface p-4 shadow-soft">
                    <div className="flex items-center gap-2 text-sm font-extrabold text-text-primary">
                      <Star size={18} className="fill-yellow-500 text-yellow-500" />
                      2,000+ book ideas
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-text-secondary">
                      Search less. Pick a better next read faster.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-5 left-5 hidden rounded-lg bg-primary-500 px-4 py-3 text-sm font-bold text-white shadow-large sm:flex sm:items-center sm:gap-2">
              <Sparkles size={17} />
              Designed for daily reading rhythm
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-20">
      <div className="container-custom">
        <div className="mb-10 max-w-2xl">
          <p className="mb-3 text-sm font-bold uppercase text-primary-700">What Bukito replaces</p>
          <h2 className="text-3xl font-extrabold text-text-primary sm:text-4xl">
            Less scattered book advice. More confident family reading.
          </h2>
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          {featureCards.map(({ icon: Icon, title, copy, color }) => (
            <div key={title} className="rounded-lg border border-gray-100 bg-bg-surface p-6 shadow-soft">
              <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-lg ${color}`}>
                <Icon size={23} className={color.includes('text-text-primary') ? '' : 'text-white'} />
              </div>
              <h3 className="text-xl font-extrabold text-text-primary">{title}</h3>
              <p className="mt-3 leading-relaxed text-text-secondary">{copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-y border-primary-100 bg-primary-50 py-16 lg:py-20">
      <div className="container-custom">
        <div className="grid gap-10 lg:grid-cols-[360px_1fr] lg:items-center">
          <div>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-bg-surface text-primary-700 shadow-soft">
              <Search size={23} />
            </div>
            <h2 className="text-3xl font-extrabold text-text-primary sm:text-4xl">
              Start from a curated list, not a blank shelf.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-text-secondary">
              Browse bedtime picks, confidence builders, funny read-alouds and stage-aware discoveries pulled from the Bukito library.
            </p>
            <Link
              to="/lists"
              className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-primary-500 px-5 text-sm font-bold text-white shadow-button transition-colors hover:bg-primary-600"
            >
              Browse all lists
              <ArrowRight size={17} />
            </Link>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {[
              ['Bedtime calm', 'Soft read-alouds for winding down.'],
              ['First readers', 'Simple, satisfying books for early confidence.'],
              ['Laugh together', 'Funny picks that make one more page easy.']
            ].map(([title, copy]) => (
              <div key={title} className="rounded-lg bg-bg-surface p-5 shadow-soft">
                <h3 className="font-extrabold text-text-primary">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <NewReleasesFeed />

    <section className="bg-primary-500 py-16 text-white lg:py-20">
      <div className="container-custom">
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <h2 className="max-w-3xl text-3xl font-extrabold sm:text-4xl lg:text-5xl">
              Bring the next good book into tonight&apos;s routine.
            </h2>
            <p className="mt-4 max-w-2xl text-lg leading-relaxed text-primary-50">
              Download Bukito to save books, track progress and keep reading recommendations close.
            </p>
          </div>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-lg bg-bg-surface px-7 py-4 text-base font-bold text-primary-700 shadow-large transition-colors hover:bg-primary-50"
          >
            <Download size={20} />
            Download Bukito
          </a>
        </div>
      </div>
    </section>

    <footer className="bg-gray-900 py-12 text-white">
      <div className="container-custom">
        <div className="grid gap-8 md:grid-cols-[1fr_auto_auto]">
          <div>
            <div className="flex items-center gap-3">
              <img src="/brand-mark.png" alt="" className="h-10 w-10 rounded-lg bg-white p-1" />
              <span className="font-display text-2xl font-semibold tracking-[0.01em] text-[#2563EB]">bukito</span>
            </div>
            <p className="mt-4 max-w-md text-gray-300">
              Helping families make reading a regular, joyful part of childhood.
            </p>
          </div>

          <div>
            <h4 className="font-bold">Explore</h4>
            <div className="mt-3 space-y-2 text-gray-300">
              <Link to="/" className="block hover:text-white">Home</Link>
              <Link to="/lists" className="block hover:text-white">Book lists</Link>
            </div>
          </div>

          <div>
            <h4 className="font-bold">Support</h4>
            <div className="mt-3 space-y-2 text-gray-300">
              <a href="mailto:hello@bukitoapp.com" className="block hover:text-white">hello@bukitoapp.com</a>
            </div>
          </div>
        </div>

        <div className="mt-8 border-t border-gray-700 pt-8 text-sm text-gray-400">
          <p>Copyright 2026 Bukito. Built with care for families who love books.</p>
        </div>
      </div>
    </footer>
  </div>
)

export default Homepage
